/**
 * Generated by orval v7.17.0 🍺
 * Do not edit manually.
 * FieldPay API
 * FieldPay API for managing organizations, opportunities, payouts and talent management
 * OpenAPI spec version: v1
 */
import { useQuery } from "@tanstack/react-query";
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import * as axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import type { ListOpportunityClaimsParams } from "../types/listOpportunityClaimsParams";

import type { OpportunityClaimDtoPaginatedResult } from "../types/opportunityClaimDtoPaginatedResult";

import type { OpportunityClaimTimelineDetailDto } from "../types/opportunityClaimTimelineDetailDto";

import type { OpportunityClaimTimelineEntryDto } from "../types/opportunityClaimTimelineEntryDto";

import type { ProblemDetails } from "../types/problemDetails";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * **Required Permission(s):** ORG_CLAIM_LIST
 */
export const listOpportunityClaims = (
  params?: ListOpportunityClaimsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityClaimDtoPaginatedResult>> => {
  return axios.default.get(`/api/opportunity-claims`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getListOpportunityClaimsQueryKey = (
  params?: ListOpportunityClaimsParams,
) => {
  return [`/api/opportunity-claims`, ...(params ? [params] : [])] as const;
};

export const getListOpportunityClaimsQueryOptions = <
  TData = Awaited<ReturnType<typeof listOpportunityClaims>>,
  TError = AxiosError<void>,
>(
  params?: ListOpportunityClaimsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listOpportunityClaims>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOpportunityClaimsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listOpportunityClaims>>
  > = ({ signal }) =>
    listOpportunityClaims(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listOpportunityClaims>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListOpportunityClaimsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOpportunityClaims>>
>;
export type ListOpportunityClaimsQueryError = AxiosError<void>;

export function useListOpportunityClaims<
  TData = Awaited<ReturnType<typeof listOpportunityClaims>>,
  TError = AxiosError<void>,
>(
  params?: ListOpportunityClaimsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listOpportunityClaims>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListOpportunityClaimsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_CLAIM_LIST
 */
export const getClaimTimeline = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityClaimTimelineEntryDto[]>> => {
  return axios.default.get(`/api/claims/${id}/timeline`, options);
};

export const getGetClaimTimelineQueryKey = (id?: string) => {
  return [`/api/claims/${id}/timeline`] as const;
};

export const getGetClaimTimelineQueryOptions = <
  TData = Awaited<ReturnType<typeof getClaimTimeline>>,
  TError = AxiosError<void>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getClaimTimeline>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetClaimTimelineQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getClaimTimeline>>
  > = ({ signal }) => getClaimTimeline(id, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getClaimTimeline>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetClaimTimelineQueryResult = NonNullable<
  Awaited<ReturnType<typeof getClaimTimeline>>
>;
export type GetClaimTimelineQueryError = AxiosError<void>;

export function useGetClaimTimeline<
  TData = Awaited<ReturnType<typeof getClaimTimeline>>,
  TError = AxiosError<void>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getClaimTimeline>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetClaimTimelineQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_PAYOUT_REQUEST_GET
 */
export const getClaimTimelineDetail = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityClaimTimelineDetailDto>> => {
  return axios.default.get(
    `/api/opportunity-claims/${id}/timeline-detail`,
    options,
  );
};

export const getGetClaimTimelineDetailQueryKey = (id?: string) => {
  return [`/api/opportunity-claims/${id}/timeline-detail`] as const;
};

export const getGetClaimTimelineDetailQueryOptions = <
  TData = Awaited<ReturnType<typeof getClaimTimelineDetail>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getClaimTimelineDetail>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetClaimTimelineDetailQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getClaimTimelineDetail>>
  > = ({ signal }) => getClaimTimelineDetail(id, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getClaimTimelineDetail>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetClaimTimelineDetailQueryResult = NonNullable<
  Awaited<ReturnType<typeof getClaimTimelineDetail>>
>;
export type GetClaimTimelineDetailQueryError =
  AxiosError<void | ProblemDetails>;

export function useGetClaimTimelineDetail<
  TData = Awaited<ReturnType<typeof getClaimTimelineDetail>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getClaimTimelineDetail>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetClaimTimelineDetailQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
